import { Link } from "react-router-dom";

import type { TaskOut } from "@/api/types";
import { Card } from "@/components/ui/Card";
import { Status } from "@/components/ui/Status";
import { cn } from "@/lib/cn";

/**
 * A task in a list — DS `TaskCard` (Spec 020): a status only when the task has
 * one from the set, the title, what it waits for, who has it.
 *
 * A task simply in progress gets no pill: it already sits under «В работе» (D3).
 * One that waits for another task is «Ждёт», not red — waiting is not an
 * obstacle. A result prepared by an agent is «Готово к проверке»; a finished
 * task is «Сделано» and dimmed.
 */
/**
 * `project` — the project's title (Kostya's agent, 27.09: on «Работа» the groups
 * mix projects, so every card says whose it is). Names are shown as written:
 * «у Костя» was a guessed case.
 */
export function TaskCard({ task, review = false, project }: { task: TaskOut; review?: boolean; project?: string }) {
  const done = task.completed_at != null;
  const waitsFor = task.blocked_by ?? [];
  const overdue = task.due_at && new Date(task.due_at) < new Date() && !done;
  const status = done ? "done" : review ? "review" : waitsFor.length > 0 ? "waiting" : null;
  return (
    <Link
      to={`/tasks/${task.id}`}
      aria-label={`Задача ${task.title}${
        waitsFor.length ? `, ждёт: ${waitsFor.map((b) => b.title).join(", ")}` : ""
      }`}
      className="block"
    >
      <Card className={cn("flex min-h-touch flex-col gap-2 press-surface", done && "opacity-60")}>
        {(status || overdue || project) && (
          <div className="flex flex-wrap items-center gap-2">
            {status && <Status tone={status} />}
            {project && <span className="text-meta text-p-text-muted">{project}</span>}
            {overdue && (
              <span className="rounded-full border border-p-border-strong px-2 text-chip font-semibold text-p-text">
                просрочено
              </span>
            )}
          </div>
        )}
        <span className="text-cell-title font-semibold text-p-text">{task.summary ?? task.title}</span>
        {waitsFor.length > 0 && (
          <p className="text-body text-p-text">
            <span className="text-p-text-muted">Ждёт: </span>
            {waitsFor.map((b) => `«${b.title}»`).join(", ")}
          </p>
        )}
        <p className="text-meta text-p-text-muted">
          {task.assignee_id ? `Исполнитель: ${task.assignee_name ?? "участник"}` : "Свободна · можно взять себе"}
          {task.due_at && !done && ` · до ${new Date(task.due_at).toLocaleDateString("ru-RU")}`}
        </p>
        {task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-p-surface-2 px-2 py-0.5 text-chip text-p-text-muted">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </Card>
    </Link>
  );
}
